/**
 * ApiWakeupSplash.jsx — Rê Leve IA (Etapa 12)
 *
 * Splash discreto quando a API Render Free está dormindo.
 * Faz polling do /health no fundo e some quando responde.
 * Nunca bloqueia a UI — usuário pode usar o protótipo mesmo sem API
 * (motor local assume).
 */

import { useEffect, useState } from 'react';
import { apiHealth, isApiEnabled } from '../lib/apiClient';

const POLL_INTERVAL = 3000; // 3s entre tentativas
const MAX_WAIT_MS = 60000;  // 60s antes de desistir

export default function ApiWakeupSplash() {
  const [apiAwake, setApiAwake] = useState(null); // null = checking, true/false = result
  const [elapsed, setElapsed] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isApiEnabled) {
      setApiAwake(false);
      return;
    }

    let active = true;
    const startTime = Date.now();

    async function check() {
      if (!active) return;
      const elapsedMs = Date.now() - startTime;
      setElapsed(Math.floor(elapsedMs / 1000));

      const health = await apiHealth();
      if (health && health.status === 'ok') {
        if (active) setApiAwake(true);
        return;
      }

      if (elapsedMs >= MAX_WAIT_MS) {
        if (active) setApiAwake(false); // desistiu
        return;
      }

      setTimeout(check, POLL_INTERVAL);
    }

    check();
    return () => { active = false; };
  }, []);

  // Desaparece se API acordou ou usuário fechou
  if (apiAwake === true || dismissed) return null;

  // Se API real não está habilitada, não mostra
  if (!isApiEnabled) return null;

  // API não acordou em 60s — mostra modo limitado
  if (apiAwake === false) {
    return (
      <div style={overlayStyle}>
        <div style={cardStyle}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🟡</div>
          <h3 style={{ margin: '0 0 8px', color: '#92400e' }}>
            API em modo limitado
          </h3>
          <p style={{ fontSize: 14, color: '#475569', margin: '0 0 16px', lineHeight: 1.5 }}>
            O servidor remoto não respondeu em 60s. O protótipo continua funcionando
            com o motor local. Tudo o que você precisa pra demonstração está disponível.
          </p>
          <button onClick={() => setDismissed(true)} style={buttonStyle}>
            Continuar
          </button>
        </div>
      </div>
    );
  }

  // Aguardando API
  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        <div style={{ fontSize: 36, marginBottom: 12, animation: 'pulse 1.5s infinite' }}>🌿</div>
        <h3 style={{ margin: '0 0 8px', color: '#065f46' }}>
          Acordando a Rê Leve...
        </h3>
        <p style={{ fontSize: 14, color: '#475569', margin: '0 0 12px', lineHeight: 1.5 }}>
          O servidor está acordando do plano gratuito. Isso leva uns 30 segundos.<br/>
          Você pode usar o protótipo enquanto isso — o motor local já está ativo.
        </p>
        <div style={progressContainerStyle}>
          <div style={{
            ...progressBarStyle,
            width: `${Math.min(100, (elapsed / 30) * 100)}%`,
          }} />
        </div>
        <p style={{ fontSize: 12, color: '#94a3b8', margin: '8px 0 0' }}>
          {elapsed}s
        </p>
        <button onClick={() => setDismissed(true)} style={{ ...buttonStyle, marginTop: 12 }}>
          Continuar sem esperar
        </button>
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.7; }
        }
      `}</style>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed',
  bottom: 20,
  right: 20,
  zIndex: 9999,
  maxWidth: 360,
};

const cardStyle = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: '16px 20px',
  boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
  textAlign: 'center',
};

const buttonStyle = {
  background: '#065f46',
  color: '#fff',
  border: 'none',
  padding: '6px 16px',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
};

const progressContainerStyle = {
  height: 4,
  background: '#e5e7eb',
  borderRadius: 2,
  overflow: 'hidden',
};

const progressBarStyle = {
  height: '100%',
  background: 'linear-gradient(90deg, #065f46, #10b981)',
  transition: 'width 0.5s ease',
};
